/**
 * The profile's zone and this device's zone disagree.
 *
 * This is the silent version of the midnight bug, and the worse one. A profile
 * left in the wrong zone does not produce an obvious error — it files evening
 * meals under the following day, correctly, forever, according to a zone the
 * user does not live in. Nothing looks broken. The diary simply has dinner in
 * the wrong place every single night, and because entries are bucketed at write
 * time and never recomputed, every one of them stays wrong.
 *
 * The profile row is created lazily with a guessed zone on first authenticated
 * request, so the wrong guess is the default outcome for anyone whose first
 * request came from somewhere they were passing through.
 *
 * Today can see both zones, so Today says so. The offer is genuinely two-sided:
 * a traveller *should* keep their home zone, because their diary is a record of
 * days at home, and dismissing this is a legitimate answer rather than a
 * postponement.
 *
 * Because it is a real answer, it is kept — on the device that gave it, keyed
 * by the pair of zones it was about; see `zone-answer`. Devices are what
 * disagree here (a phone that travels, a desktop stuck on UTC), so the answer
 * belongs to one of them rather than to the account, and this component says as
 * much in its own copy: an offer that quietly returned on the next load would
 * be a promise broken in writing.
 */

import { hoursApart } from "@calorio/core";

interface TimezoneMismatchProps {
  profileZone: string;
  deviceZone: string;
  onAdopt: () => void;
  onKeep: () => void;
  saving?: boolean;
}

export function TimezoneMismatch({
  profileZone,
  deviceZone,
  onAdopt,
  onKeep,
  saving = false,
}: TimezoneMismatchProps) {
  const apart = hoursApart(profileZone, deviceZone);

  return (
    <div className="tzwarn">
      <span className="tzwarn__label">Check your timezone</span>

      <p className="tzwarn__body">
        Your profile says <span className="num">{profileZone}</span>, this device says{" "}
        <span className="num">{deviceZone}</span>.
        {apart != null && apart > 0 && (
          <>
            {" "}
            <span className="num">{apart}</span> {apart === 1 ? "hour" : "hours"} apart means an
            evening meal here files under tomorrow there.
          </>
        )}
      </p>

      <div className="tzwarn__choice">
        <button className="tzwarn__adopt" onClick={onAdopt} disabled={saving}>
          {saving ? "Saving…" : `Use ${deviceZone}`}
        </button>
        <button className="tzwarn__keep" onClick={onKeep} disabled={saving}>
          I&rsquo;m travelling — keep it
        </button>
      </div>

      {/* The reassurance that makes the choice safe to make. Without it,
          changing a timezone looks like it might rewrite history. */}
      <p className="tzwarn__note">
        Entries already logged keep the day they were filed under. Changing the zone only moves
        which date counts as today. Keeping it is remembered on this device, so this won&rsquo;t
        be asked again here.
      </p>
    </div>
  );
}
